import { useState } from 'react';
import { X, ChevronDown, ChevronUp, Copy, Check, Mail, Calendar, Phone, MapPin, AlertTriangle, ExternalLink } from 'lucide-react';
import VibeScoreRating from './VibeScoreRating';

interface Application {
  id: string;
  email: string;
  created_at: string;
  submitted_at?: string;
  status: string;
  previous_status?: string;
  status_changed_at?: string;
  status_history?: Array<{
    status: string;
    previous_status: string;
    changed_at: string;
    changed_by: string;
  }>;
  full_name?: string;
  phone?: string;
  discord_handle?: string;
  location_timezone?: string;
  excitement_areas?: string;
  tools_software?: string;
  creative_projects?: string;
  decision_story?: string;
  stuck_process?: string;
  feedback_preference?: string;
  trust_vs_communication?: string;
  wrong_moment?: string;
  motivation_method?: string;
  speed_vs_perfection?: string;
  recent_skill?: string;
  week_skill_approach?: string;
  intimidating_skill?: string;
  team_environment?: string;
  handle_disagreements?: string;
  tight_deadline_version?: string;
  excitement_small_team?: string;
  meaningful_collaboration?: string;
  not_on_resume?: string;
  anything_else?: string;
  portfolio_links?: string;
  desired_compensation?: string;
  unpaid_profit_share?: string;
  ai_opinion?: string;
  ai_tools_used?: string;
  ai_useful_areas?: string;
  ai_avoid_areas?: string;
  tool_vs_manual?: string;
  calendly_scheduled_at?: string;
  books_qa?: number;
  content_qa?: number;
  accountability_rating?: number;
  honesty_about_mistakes_rating?: number;
  downplay_mistakes_rating?: number;
  leadership_initiative_rating?: number;
  preferred_engine?: string;
  primary_role?: string;
  secondary_role?: string;
  score_total?: number;
  score_breakdown?: string;
  score_reason?: string;
  scored_at?: string;
  manual_status_override?: boolean;
  manual_override_at?: string;
  manual_override_by?: string;
  original_scored_status?: string;
  cancel_agreement?: boolean;
  interview_question?: string;
  score_overall?: number;
  score_mindset?: number;
  score_self_starter?: number;
  score_self_learning?: number;
  score_collaboration?: number;
  flags?: string[];
  rationale_llm?: string;
  decision_llm?: string;
  summary_llm?: string;
  ai_stance?: string;
  vibe_score?: number;
  vibe_score_notes?: string;
  vibe_score_updated_at?: string;
  [key: string]: any;
}

interface ApplicationModalProps {
  application: Application;
  applicationType: 'podcast' | 'writer' | 'game_dev' | 'moderator';
  statusOptions: string[];
  onClose: () => void;
  onStatusUpdate: (id: string, status: string) => Promise<void>;
  onVibeScoreUpdate: (id: string, score: number, notes: string) => Promise<void>;
  onNext?: () => void;
  onPrevious?: () => void;
}

export default function ApplicationModal({
  application,
  applicationType,
  statusOptions,
  onClose,
  onStatusUpdate,
  onVibeScoreUpdate,
  onNext,
  onPrevious,
}: ApplicationModalProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(application.status === 'Scheduled' ? ['interview', 'keyMetrics'] : ['keyMetrics'])
  );
  const [copiedEmail, setCopiedEmail] = useState(false);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(application.email);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } catch (error) {
      console.error('Failed to copy email:', error);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    await onStatusUpdate(application.id, newStatus);
  };

  const handleVibeScoreUpdate = async (score: number, notes: string) => {
    await onVibeScoreUpdate(application.id, score, notes);
  };

  const renderGameDevContent = () => (
    <>
      {(application.flags && application.flags.length > 0) || application.cancel_agreement ? (
        <FlagsAlert
          flags={application.flags || []}
          cancelAgreement={application.cancel_agreement || false}
        />
      ) : null}

      <div className="bg-gray-800/40 border border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-gray-300 font-semibold text-sm uppercase tracking-wide">Contact Information</h4>
          <button
            onClick={copyEmail}
            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded text-xs transition-colors"
          >
            {copiedEmail ? <Check size={14} /> : <Copy size={14} />}
            {copiedEmail ? 'Copied!' : 'Copy Email'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Mail size={14} className="text-gray-400 flex-shrink-0" />
            <div>
              <div className="text-gray-400 text-xs">Email</div>
              <div className="text-white font-medium">{application.email}</div>
            </div>
          </div>
          {application.full_name && (
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 bg-gray-600 rounded-full flex-shrink-0" />
              <div>
                <div className="text-gray-400 text-xs">Name</div>
                <div className="text-white font-medium">{application.full_name}</div>
              </div>
            </div>
          )}
          {application.phone && (
            <div className="flex items-center gap-2">
              <Phone size={14} className="text-gray-400 flex-shrink-0" />
              <div>
                <div className="text-gray-400 text-xs">Phone</div>
                <div className="text-white font-medium">{application.phone}</div>
              </div>
            </div>
          )}
          {application.discord_handle && (
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 bg-indigo-500 rounded-full flex-shrink-0" />
              <div>
                <div className="text-gray-400 text-xs">Discord</div>
                <div className="text-white font-medium">{application.discord_handle}</div>
              </div>
            </div>
          )}
          {application.location_timezone && (
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-gray-400 flex-shrink-0" />
              <div>
                <div className="text-gray-400 text-xs">Location/Timezone</div>
                <div className="text-white font-medium">{application.location_timezone}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {(application.status === 'Scheduled' || application.interview_question || application.calendly_scheduled_at) && (
        <div className="bg-gradient-to-br from-blue-900/20 to-transparent border border-blue-400/40 rounded-lg p-5">
          <h4 className="text-blue-400 font-bold text-lg mb-3 flex items-center gap-2">
            Interview Details
            <span className="text-xs px-2 py-1 rounded bg-blue-600 text-white">Scheduled</span>
          </h4>
          {application.calendly_scheduled_at && (
            <div className="bg-gray-900/50 rounded-lg p-3 border border-blue-400/20 mb-3 flex items-center gap-2">
              <Calendar size={16} className="text-blue-400" />
              <span className="text-gray-300 text-sm">
                Scheduled: {new Date(application.calendly_scheduled_at).toLocaleString()}
              </span>
            </div>
          )}
          {application.interview_question && (
            <div className="bg-gray-900/50 rounded-lg p-4 border border-blue-400/20">
              <p className="text-gray-200 whitespace-pre-wrap leading-relaxed">{application.interview_question}</p>
            </div>
          )}
        </div>
      )}

      <VibeScoreRating
        score={application.vibe_score || null}
        notes={application.vibe_score_notes || null}
        updatedAt={application.vibe_score_updated_at || null}
        onUpdate={handleVibeScoreUpdate}
      />

      <CollapsibleSection
        title="Key Metrics"
        expanded={expandedSections.has('keyMetrics')}
        onToggle={() => toggleSection('keyMetrics')}
        theme="default"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {application.score_total !== undefined && application.score_total !== null && (
            <div className="bg-gray-800/50 p-4 rounded-lg border border-blue-400/20">
              <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Step 2 Score</div>
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${
                  application.score_total >= 42 ? 'text-green-400' :
                  application.score_total >= 31 ? 'text-yellow-400' :
                  'text-red-400'
                }`}>
                  {application.score_total}
                </span>
                <span className="text-gray-500 text-sm">/52</span>
              </div>
              {application.scored_at && (
                <div className="text-gray-500 text-xs mt-1">
                  {new Date(application.scored_at).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
          {application.score_overall && (
            <div className="bg-gray-800/50 p-4 rounded-lg border border-purple-400/20">
              <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Overall Score</div>
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${
                  application.score_overall >= 80 ? 'text-green-400' :
                  application.score_overall >= 60 ? 'text-yellow-400' :
                  'text-red-400'
                }`}>
                  {application.score_overall}
                </span>
                <span className="text-gray-500 text-sm">/100</span>
              </div>
            </div>
          )}
          <div className="bg-gray-800/50 p-4 rounded-lg">
            <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Primary Role</div>
            <div className="text-white font-medium">{application.primary_role || 'Not specified'}</div>
            {application.secondary_role && (
              <div className="text-gray-400 text-xs mt-0.5">+ {application.secondary_role}</div>
            )}
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg">
            <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">Engine</div>
            <div className="text-white font-medium">{application.preferred_engine || 'Not specified'}</div>
          </div>
        </div>

        {application.score_reason && (
          <div className="mt-3">
            <ExpandableText
              label="Score Reasoning"
              text={application.score_reason}
              maxLength={200}
            />
          </div>
        )}

        {application.manual_status_override && (
          <div className="mt-4 bg-amber-900/20 border border-amber-400/40 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-amber-400" />
              <span className="text-amber-400 font-bold text-sm">Manual Override Active</span>
            </div>
            {application.original_scored_status && (
              <div className="text-gray-300 text-xs mb-1">
                Original Status: <span className="text-white font-medium">{application.original_scored_status}</span>
              </div>
            )}
            {application.manual_override_at && (
              <div className="text-gray-400 text-xs mb-1">
                Override Date: {new Date(application.manual_override_at).toLocaleString()}
              </div>
            )}
            {application.manual_override_by && (
              <div className="text-gray-400 text-xs">
                Override By: {application.manual_override_by}
              </div>
            )}
          </div>
        )}
      </CollapsibleSection>

      {(application.score_mindset || application.score_self_starter || application.decision_llm || application.rationale_llm || application.summary_llm) && (
        <CollapsibleSection
          title="AI Initial Assessment"
          expanded={expandedSections.has('initialScores')}
          onToggle={() => toggleSection('initialScores')}
          theme="default"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {application.score_mindset && (
                <ScoreBox label="Mindset" score={application.score_mindset} />
              )}
              {application.score_self_starter && (
                <ScoreBox label="Self Starter" score={application.score_self_starter} />
              )}
              {application.score_self_learning && (
                <ScoreBox label="Self Learning" score={application.score_self_learning} />
              )}
              {application.score_collaboration && (
                <ScoreBox label="Collaboration" score={application.score_collaboration} />
              )}
            </div>
            {application.decision_llm && (
              <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400 text-xs font-bold uppercase">AI Decision: </span>
                  <span className={`font-bold ${
                    application.decision_llm === 'Approved' ? 'text-green-400' :
                    application.decision_llm === 'Interview' ? 'text-amber-400' :
                    'text-red-400'
                  }`}>
                    {application.decision_llm}
                  </span>
                  {application.ai_stance && (
                    <span className="text-xs px-2 py-0.5 rounded bg-purple-900/50 text-purple-300 border border-purple-700">
                      {application.ai_stance}
                    </span>
                  )}
                </div>
              </div>
            )}
            {application.rationale_llm && (
              <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700">
                <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">AI Rationale</div>
                <p className="text-gray-300 text-sm whitespace-pre-wrap">{application.rationale_llm}</p>
              </div>
            )}
            {application.summary_llm && (
              <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700">
                <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">AI Summary</div>
                <p className="text-gray-300 text-sm">{application.summary_llm}</p>
              </div>
            )}
          </div>
        </CollapsibleSection>
      )}

      {application.score_total && (
        <CollapsibleSection
          title="Step 2 Assessment Details"
          expanded={expandedSections.has('step2Details')}
          onToggle={() => toggleSection('step2Details')}
          theme="default"
        >
          <div className="flex flex-wrap gap-2">
            {application.books_qa !== undefined && <ScoreBadge label="Books" score={application.books_qa} />}
            {application.content_qa !== undefined && <ScoreBadge label="Content" score={application.content_qa} />}
            {application.accountability_rating !== undefined && <ScoreBadge label="Accountability" score={application.accountability_rating} />}
            {application.honesty_about_mistakes_rating !== undefined && <ScoreBadge label="Honesty" score={application.honesty_about_mistakes_rating} />}
            {application.downplay_mistakes_rating !== undefined && <ScoreBadge label="Downplay Mistakes" score={application.downplay_mistakes_rating} />}
            {application.leadership_initiative_rating !== undefined && <ScoreBadge label="Leadership" score={application.leadership_initiative_rating} />}
          </div>
        </CollapsibleSection>
      )}

      {(application.excitement_areas || application.tools_software || application.creative_projects || application.portfolio_links) && (
        <CollapsibleSection
          title="Interests & Technical Skills"
          expanded={expandedSections.has('interests')}
          onToggle={() => toggleSection('interests')}
          theme="blue"
        >
          {application.excitement_areas && <TextArea label="Excitement Areas in Game Dev" value={application.excitement_areas} />}
          {application.tools_software && <TextArea label="Tools & Software" value={application.tools_software} />}
          {application.creative_projects && <TextArea label="Creative Projects" value={application.creative_projects} />}
          {application.portfolio_links && <TextArea label="Portfolio Links" value={application.portfolio_links} />}
        </CollapsibleSection>
      )}

      {(application.decision_story || application.stuck_process || application.feedback_preference || application.trust_vs_communication || application.speed_vs_perfection) && (
        <CollapsibleSection
          title="Work Style & Decision Making"
          expanded={expandedSections.has('workStyle')}
          onToggle={() => toggleSection('workStyle')}
          theme="default"
        >
          {application.decision_story && <TextArea label="Decision Making Story" value={application.decision_story} />}
          {application.stuck_process && <TextArea label="Getting Unstuck" value={application.stuck_process} />}
          {application.feedback_preference && <TextArea label="Feedback Preference" value={application.feedback_preference} />}
          {application.trust_vs_communication && <TextArea label="Trust vs Communication" value={application.trust_vs_communication} />}
          {application.speed_vs_perfection && <TextArea label="Speed vs Perfection" value={application.speed_vs_perfection} />}
        </CollapsibleSection>
      )}

      {(application.recent_skill || application.week_skill_approach || application.intimidating_skill) && (
        <CollapsibleSection
          title="Learning & Growth"
          expanded={expandedSections.has('learning')}
          onToggle={() => toggleSection('learning')}
          theme="green"
        >
          {application.recent_skill && <TextArea label="Recently Learned Skill" value={application.recent_skill} />}
          {application.week_skill_approach && <TextArea label="One Week Learning Approach" value={application.week_skill_approach} />}
          {application.intimidating_skill && <TextArea label="Most Intimidating Skill" value={application.intimidating_skill} />}
        </CollapsibleSection>
      )}

      {(application.team_environment || application.handle_disagreements || application.tight_deadline_version || application.excitement_small_team || application.meaningful_collaboration) && (
        <CollapsibleSection
          title="Team Dynamics & Collaboration"
          expanded={expandedSections.has('team')}
          onToggle={() => toggleSection('team')}
          theme="cyan"
        >
          {application.team_environment && <TextArea label="Ideal Team Environment" value={application.team_environment} />}
          {application.handle_disagreements && <TextArea label="Handling Disagreements" value={application.handle_disagreements} />}
          {application.tight_deadline_version && <TextArea label="Tight Deadline Approach" value={application.tight_deadline_version} />}
          {application.excitement_small_team && <TextArea label="Excitement About Small Teams" value={application.excitement_small_team} />}
          {application.meaningful_collaboration && <TextArea label="Meaningful Collaboration Example" value={application.meaningful_collaboration} />}
        </CollapsibleSection>
      )}

      {(application.ai_opinion || application.ai_tools_used || application.ai_useful_areas || application.ai_avoid_areas || application.tool_vs_manual) && (
        <CollapsibleSection
          title="AI & Technology Philosophy"
          expanded={expandedSections.has('aiPhilosophy')}
          onToggle={() => toggleSection('aiPhilosophy')}
          theme="purple"
        >
          {application.ai_opinion && <TextArea label="Opinion on AI" value={application.ai_opinion} />}
          {application.ai_tools_used && <TextArea label="AI Tools Used" value={application.ai_tools_used} />}
          {application.ai_useful_areas && <TextArea label="Where AI is Useful" value={application.ai_useful_areas} />}
          {application.ai_avoid_areas && <TextArea label="Where to Avoid AI" value={application.ai_avoid_areas} />}
          {application.tool_vs_manual && <TextArea label="Tool vs Manual Preference" value={application.tool_vs_manual} />}
        </CollapsibleSection>
      )}

      {(application.wrong_moment || application.motivation_method || application.not_on_resume || application.anything_else) && (
        <CollapsibleSection
          title="Personal Philosophy"
          expanded={expandedSections.has('philosophy')}
          onToggle={() => toggleSection('philosophy')}
          theme="orange"
        >
          {application.wrong_moment && <TextArea label="Wrong Moment Story" value={application.wrong_moment} />}
          {application.motivation_method && <TextArea label="Staying Motivated" value={application.motivation_method} />}
          {application.not_on_resume && <TextArea label="Not on Resume" value={application.not_on_resume} />}
          {application.anything_else && <TextArea label="Anything Else" value={application.anything_else} />}
        </CollapsibleSection>
      )}

      {(application.desired_compensation || application.unpaid_profit_share) && (
        <CollapsibleSection
          title="Compensation & Commitment"
          expanded={expandedSections.has('compensation')}
          onToggle={() => toggleSection('compensation')}
          theme="amber"
        >
          {application.desired_compensation && <InfoField label="Desired Compensation" value={application.desired_compensation} />}
          {application.unpaid_profit_share && <TextArea label="Thoughts on Unpaid Profit-Share Work" value={application.unpaid_profit_share} />}
        </CollapsibleSection>
      )}

      {application.status_history && application.status_history.length > 0 && (
        <CollapsibleSection
          title="Status History & Administrative"
          expanded={expandedSections.has('statusHistory')}
          onToggle={() => toggleSection('statusHistory')}
          theme="default"
        >
          <div className="space-y-3">
            {application.status_history.map((entry, index) => (
              <div key={index} className="bg-gray-800/30 rounded-lg p-3 border border-gray-700">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">From:</span>
                    <span className="text-gray-300 font-medium text-sm">{entry.previous_status}</span>
                    <span className="text-gray-500">→</span>
                    <span className="text-white font-medium text-sm">{entry.status}</span>
                  </div>
                  <span className="text-gray-500 text-xs">
                    {new Date(entry.changed_at).toLocaleString()}
                  </span>
                </div>
                {entry.changed_by && (
                  <div className="text-gray-400 text-xs">
                    Changed by: {entry.changed_by}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}
    </>
  );

  const renderPodcastContent = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-yellow-400 font-bold mb-3">Personal Info</h4>
          <div className="space-y-2 text-sm">
            <InfoField label="Name" value={`${application.first_name} ${application.last_name}`} />
            <InfoField label="Alias" value={application.alias} />
            <InfoField label="English Speaking" value={application.english_speaking ? 'Yes' : 'No'} />
          </div>
        </div>
        <div>
          <h4 className="text-yellow-400 font-bold mb-3">Project Info</h4>
          <InfoField label="Project Link" value={application.project_link} />
        </div>
      </div>
      <TextArea label="About You" value={application.about_you} />
      <TextArea label="About Project" value={application.about_project} />
    </>
  );

  const renderWriterContent = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InfoField label="Name" value={application.full_name} />
        <InfoField label="Discord" value={application.discord_username || 'N/A'} />
        <InfoField label="Experience" value={application.experience_level} />
        <InfoField label="Collaboration" value={application.collaboration_open ? 'Open' : 'Not Open'} />
      </div>
      <TextArea label="Writing Interests" value={application.writing_interests} />
      <TextArea label="Portfolio Links" value={application.portfolio_links || 'None provided'} />
    </>
  );

  const renderModeratorContent = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InfoField label="Name" value={application.full_name} />
        <InfoField label="Discord" value={application.discord_username} />
        <InfoField label="Age" value={application.age || 'N/A'} />
        <InfoField label="Timezone" value={application.timezone_region} />
        <InfoField label="Weekly Availability" value={application.weekly_availability} />
      </div>
      <TextArea label="Experience" value={application.moderation_experience} />
      <TextArea label="Why WFP" value={application.why_wfp} />
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div
        className="absolute inset-0 bg-black/90 backdrop-blur-md"
        onClick={onClose}
      />

      <div className="relative bg-gray-950 border border-yellow-400/30 rounded-xl max-w-6xl w-full max-h-[90vh] flex flex-col shadow-2xl animate-slideUp">
        <div className="sticky top-0 z-10 bg-gradient-to-r from-gray-900 to-black border-b border-yellow-400/30 px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h2 className="text-white font-bold text-xl mb-1">
                {application.full_name || `${application.first_name} ${application.last_name}` || application.email}
              </h2>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-gray-400">
                  <Mail size={14} />
                  {application.email}
                  <button
                    onClick={copyEmail}
                    className="ml-1 text-gray-400 hover:text-yellow-400 transition-colors"
                  >
                    {copiedEmail ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </span>
                <span className="flex items-center gap-1 text-gray-400">
                  <Calendar size={14} />
                  {new Date(application.submitted_at || application.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {onPrevious && (
                <button
                  onClick={onPrevious}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                >
                  ← Previous
                </button>
              )}
              {onNext && (
                <button
                  onClick={onNext}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                >
                  Next →
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-red-600/20 rounded-lg transition-colors"
              >
                <X className="text-yellow-400" size={24} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-gray-400 text-sm font-medium mb-2 block">Status</label>
              <select
                value={application.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg font-medium border-2 border-gray-600 hover:border-yellow-400 focus:outline-none focus:border-yellow-400 transition-colors cursor-pointer"
              >
                {statusOptions.map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            {application.previous_status && (
              <div className="flex-1">
                <div className="text-gray-400 text-sm font-medium mb-2">Previous Status</div>
                <div className="bg-gray-800/50 px-4 py-2 rounded-lg border border-gray-700">
                  <span className="text-gray-300">{application.previous_status}</span>
                  {application.status_changed_at && (
                    <span className="text-gray-500 text-xs ml-2">
                      {new Date(application.status_changed_at).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {applicationType === 'game_dev' && renderGameDevContent()}
          {applicationType === 'podcast' && renderPodcastContent()}
          {applicationType === 'writer' && renderWriterContent()}
          {applicationType === 'moderator' && renderModeratorContent()}
        </div>
      </div>
    </div>
  );
}

function FlagsAlert({ flags, cancelAgreement }: { flags: string[]; cancelAgreement: boolean }) {
  if (!flags.length && !cancelAgreement) return null;

  return (
    <div className="bg-gradient-to-br from-red-900/40 to-red-900/10 border-2 border-red-500/60 rounded-lg p-5">
      <div className="flex items-start gap-3">
        <AlertTriangle size={24} className="text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-red-400 font-bold text-lg mb-2">Alerts & Flags</h4>
          {cancelAgreement && (
            <div className="mb-3 inline-block px-3 py-1 bg-red-600 text-white rounded-lg font-bold text-sm">
              Cancelled Agreement
            </div>
          )}
          {flags.length > 0 && (
            <ul className="space-y-2">
              {flags.map((flag, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-red-400 mt-1">•</span>
                  <span className="text-gray-200">{flag}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function ExpandableText({ label, text, maxLength = 200 }: { label: string; text: string; maxLength?: number }) {
  const [expanded, setExpanded] = useState(false);
  const shouldTruncate = text.length > maxLength;
  const displayText = expanded || !shouldTruncate ? text : text.slice(0, maxLength) + '...';

  return (
    <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700">
      <div className="text-gray-400 text-xs uppercase tracking-wide mb-1">{label}</div>
      <p className="text-gray-300 text-sm whitespace-pre-wrap">{displayText}</p>
      {shouldTruncate && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-blue-400 hover:text-blue-300 text-xs mt-2 font-medium transition-colors"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  theme?: 'default' | 'blue' | 'green' | 'cyan' | 'purple' | 'orange' | 'amber';
}

function CollapsibleSection({ title, children, expanded, onToggle, theme = 'default' }: CollapsibleSectionProps) {
  const themeClasses = {
    default: {
      border: 'border-gray-700',
      headerBg: 'bg-gray-800/50 hover:bg-gray-800',
      title: 'text-yellow-400',
      icon: 'text-yellow-400',
    },
    blue: {
      border: 'border-blue-700/50',
      headerBg: 'bg-blue-900/30 hover:bg-blue-900/40',
      title: 'text-blue-400',
      icon: 'text-blue-400',
    },
    green: {
      border: 'border-green-700/50',
      headerBg: 'bg-green-900/30 hover:bg-green-900/40',
      title: 'text-green-400',
      icon: 'text-green-400',
    },
    cyan: {
      border: 'border-cyan-700/50',
      headerBg: 'bg-cyan-900/30 hover:bg-cyan-900/40',
      title: 'text-cyan-400',
      icon: 'text-cyan-400',
    },
    purple: {
      border: 'border-purple-700/50',
      headerBg: 'bg-purple-900/30 hover:bg-purple-900/40',
      title: 'text-purple-400',
      icon: 'text-purple-400',
    },
    orange: {
      border: 'border-orange-700/50',
      headerBg: 'bg-orange-900/30 hover:bg-orange-900/40',
      title: 'text-orange-400',
      icon: 'text-orange-400',
    },
    amber: {
      border: 'border-amber-700/50',
      headerBg: 'bg-amber-900/30 hover:bg-amber-900/40',
      title: 'text-amber-400',
      icon: 'text-amber-400',
    },
  };

  const colors = themeClasses[theme];

  return (
    <div className={`bg-gray-900/30 border ${colors.border} rounded-lg overflow-hidden`}>
      <button
        onClick={onToggle}
        className={`w-full px-5 py-3 flex items-center justify-between ${colors.headerBg} transition-colors`}
      >
        <h4 className={`${colors.title} font-bold text-base`}>{title}</h4>
        {expanded ? <ChevronUp size={20} className={colors.icon} /> : <ChevronDown size={20} className={colors.icon} />}
      </button>
      {expanded && (
        <div className="p-5 space-y-4">
          {children}
        </div>
      )}
    </div>
  );
}

function ScoreBox({ label, score }: { label: string; score: number }) {
  return (
    <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700">
      <div className="text-gray-400 text-xs mb-1">{label}</div>
      <div className="text-white text-xl font-bold">{score}</div>
    </div>
  );
}

function ScoreBadge({ label, score }: { label: string; score: number }) {
  return (
    <div className="inline-flex items-center gap-2 bg-gray-800/50 px-3 py-1.5 rounded-lg border border-gray-700">
      <span className="text-gray-400 text-xs font-medium">{label}:</span>
      <span className="text-white text-sm font-bold">{score}</span>
    </div>
  );
}

function InfoField({ label, value, icon }: { label: string; value: any; icon?: React.ReactNode }) {
  const displayValue = typeof value === 'object' && value !== null
    ? JSON.stringify(value)
    : (value ?? 'Not provided');

  return (
    <div>
      <span className="text-gray-400 text-sm">{label}: </span>
      <span className="text-white font-medium flex items-center gap-1">
        {icon}
        {displayValue}
      </span>
    </div>
  );
}

function TextArea({ label, value }: { label: string; value: any }) {
  const displayValue = typeof value === 'object' && value !== null
    ? JSON.stringify(value, null, 2)
    : value || 'Not provided';

  const isJson = typeof value === 'object' && value !== null;

  return (
    <div>
      <div className="text-gray-400 font-bold mb-1 text-sm">{label}</div>
      <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700">
        {isJson ? (
          <pre className="text-gray-300 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
            {displayValue}
          </pre>
        ) : (
          <p className="text-gray-300 whitespace-pre-wrap text-sm leading-relaxed">{displayValue}</p>
        )}
      </div>
    </div>
  );
}
